import {
  EuiButton,
  EuiDescriptionList,
  EuiDescriptionListProps,
  EuiFlexGroup,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiTab,
  EuiTabs,
} from "@inscopix/ideas-eui";
import assert from "assert";
import { ModalError } from "components/ModalError/ModalError";
import { RunStatusBadge } from "components/RunStatusBadge/RunStatusBadge";
import { ShortTaskId } from "components/ShortTaskId/ShortTaskId";
import moment from "moment";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { isWorkflowTable } from "pages/gdt/store/DataTableProvider.helpers";
import { useState } from "react";
import { TabLogs } from "./TabLogs/TabLogs";
import { TabResourceUsage } from "./TabResourceUsage/TabResourceUsage";
import { TabStatusGraph } from "./TabStatusGraph/TabStatusGraph";

export type ModalRunProgressTab = "LOGS" | "GRAPH" | "RESOURCE_USAGE";

export interface ModalRunProgressProps {
  onClose: () => void;
  rowId: string;
  initialTab?: ModalRunProgressTab;
}

export const ModalRunProgress = ({
  onClose,
  rowId,
  initialTab = "GRAPH",
}: ModalRunProgressProps) => {
  const row = useDataTableContext((s) => {
    const row = s.tables
      .filter(isWorkflowTable)
      .flatMap((t) => t.rows)
      .find((r) => r.id === rowId);
    assert(row !== undefined);
    return row;
  });

  const run = row.run;

  const [selectedTab, setSelectedTab] =
    useState<ModalRunProgressTab>(initialTab);

  if (run === null) {
    return <ModalError title="Run info not found" onClose={onClose} />;
  }

  const taskInfo: NonNullable<EuiDescriptionListProps["listItems"]> = [
    {
      title: "Run ID",
      description: <ShortTaskId taskId={run.id} />,
    },
    {
      title: "Run Status",
      description: <RunStatusBadge runStatus={run.status} />,
    },
    {
      title: "Date created",
      description: moment(run.date_created).format("MMMM Do YYYY, h:mm:ss a"),
    },
  ];

  const tabs: { id: ModalRunProgressTab; name: string }[] = [
    {
      id: "GRAPH",
      name: "Status",
    },
    {
      id: "LOGS",
      name: "Logs",
    },
    {
      id: "RESOURCE_USAGE",
      name: "Resource Usage",
    },
  ];

  return (
    <EuiModal
      style={{ height: 800, width: 800, minHeight: 600 }}
      onClose={onClose}
    >
      <EuiModalHeader>
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiDescriptionList type="column" listItems={taskInfo} />
          <EuiTabs>
            {tabs.map((tab) => (
              <EuiTab
                key={tab.id}
                id={tab.id}
                isSelected={selectedTab === tab.id}
                onClick={() => setSelectedTab(tab.id)}
              >
                {tab.name}
              </EuiTab>
            ))}
          </EuiTabs>
        </EuiFlexGroup>
      </EuiModalHeader>

      <EuiModalBody>
        {(() => {
          switch (selectedTab) {
            case "LOGS": {
              return <TabLogs progress={run.progress} />;
            }
            case "GRAPH": {
              return <TabStatusGraph progress={run.progress} />;
            }
            case "RESOURCE_USAGE": {
              return <TabResourceUsage progress={run.progress} />;
            }
          }
        })()}
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButton onClick={onClose} fill>
          Close
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
